// tslint:disable:max-file-line-count
import { mergeStyles } from '@cian/utils';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { checkTouchDevice } from '../../browser/utils/dom';
import { VillageSummary } from '../offer-card/components/VillageSummary';
import { offerHelper } from '../presenter';
import { IOfferPhoto, ISimilar } from '../types/offer';
import { getPromotionsAnalyticsData } from '../utils/getPromotionsAnalyticsData';
import { canUseHiddenBase } from '../utils/user';

import { CallbackModalContainer } from '../common/components/callback_modal';
import {
  Comments,
} from '../common/components/comments';
import { GalleryImage } from '../common/components/gallery/image';
import { GalleryItemPreloader } from '../common/components/gallery/preloader';
import { HiddenBaseLabel } from '../common/components/hidden_base_label';
import { Map } from '../common/components/map';
import { IOfferCardContext, IOfferCardProps } from '../offer-card';
import { Address } from '../offer-card/components/address';
import { AgentBrand } from '../offer-card/components/AgentBrand';
import { Deadline } from '../offer-card/components/deadline';
import { JkName } from '../offer-card/components/jk_name';
import { LandArea } from '../offer-card/components/land-area';
import { LandStatus } from '../offer-card/components/land-status';
import { LeasePeriod } from '../offer-card/components/lease_period';
import { Pessimisation } from '../offer-card/components/pessimisation';
import { Photos } from '../offer-card/components/photos';
import { RightPanel } from '../common/components/RightPanel';
import { HorizontalAgentBrand } from './HorizontalAgentBrand';
import { PreviewPlaceholderIcon } from './icons/PreviewPlaceholderIcon';
import { TopButtons } from './TopButtons';
import { TopDescription } from './TopDescription';
import { TopPrice } from './TopPrice';
import { TopTitle } from './TopTitle';
import { getAgentPhone } from '../utils/getAgentPhone';

import * as offerCardStyles from '../offer-card/offerCard.css';
import * as styles from './TopOfferCard.css';
import { HomeownerBlock } from '../components/HomeownerBlock';
import { ExtendedOfferLabel } from '../common/components/ExtendedOfferLabel';
import { PromotionsBlock } from '../components/PromotionsBlock';
import { slicePromotions } from '../utils/slicePromotions';
import { getSimilarLinkText } from '../utils/getSimilarLinkText';
import { trackSuburbanMultiOfferClick } from '../utils/tracking';
import { isRSNewbuildingLabel } from '../utils/validators';
import { INewbuildingLabel } from '../types/newbuildingLabel';
import { CianAgency } from '../offer-card/components/CianAgency';

type TOfferMiniPhoto = IOfferPhoto | null;

export interface ITopOfferCardState {
  isTouchDevice: boolean;
  galleryControlsHidden: boolean;
  isCallbackModalOpened: boolean;
  isMapActive: boolean;
  isMapLoaded: boolean;
  isOfferHidden: boolean;
  isCommentAreaOpened: boolean;
  isComplainOpen: boolean;
  /** Превью фото для отображения справа от основного фото */
  previewPhotos: TOfferMiniPhoto[];
  /** Вспомогательная переменная для ссылки на остальные непоказанные фото */
  otherPhotosCount: number;
}

/**
 * Новая карточка топ-объявлений с увеличенной галереей, выделенным блоком инфо об агенте,
 * возможностью отображения заголовка объявления
 *
 * Не наследуемся от основной карточки (OfferCard) в силу большого количества структурных изменений,
 * но переиспользуем почти все компоненты
 *
 * Проект: PM-4302
 */
export class TopOfferCard extends React.Component<IOfferCardProps, ITopOfferCardState> {

  // tslint:disable-next-line:no-any
  public static childContextTypes: { [key in keyof IOfferCardContext]: PropTypes.Requireable<any> } = {
    api: PropTypes.object,
    baseUrl: PropTypes.string,
    currentSubdomain: PropTypes.string,
    defaultEmail: PropTypes.string,
    defaultPhone: PropTypes.string,
    experiments: PropTypes.array,
    httpApi: PropTypes.object,
    isMlSearchForAll: PropTypes.bool,
    isSimilar: PropTypes.bool,
    jsonQuery: PropTypes.object,
    logger: PropTypes.object,
    mlSearchRegions: PropTypes.array,
    offer: PropTypes.object,
    offerHelper: PropTypes.object,
    user: PropTypes.object,
    validateStep: PropTypes.bool,
    maxAuctionBet: PropTypes.number,
    maxAuctionService: PropTypes.string,
    auctionMaxBet: PropTypes.number,
    countrysideOffersBuilderBadge: PropTypes.bool,
    newbuildingLabel: PropTypes.object,
    onlineShowEnabled: PropTypes.bool,
  };

  public constructor(props: IOfferCardProps) {
    super(props);

    const { offer: { photos } } = props;

    /* Берем фото для превью с 1 по 5 */
    const miniPreviewPhotos: TOfferMiniPhoto[] = photos.slice(1, 5);
    /* Сохраняем, сколько фото действительно есть, чтобы реализовать плейсхолдеры */
    const previewPhotosLength = miniPreviewPhotos.length;
    /* Нам нужно 4 превью в виде фото или плейсхолдера */
    miniPreviewPhotos.length = 4;
    /* Заполняем массив до конца плейсхолдерами (null отрендерится как пустое фотое) */
    miniPreviewPhotos.fill(null, previewPhotosLength, 4);

    this.state = {
      galleryControlsHidden: true,
      isCallbackModalOpened: false,
      isCommentAreaOpened: false,
      isComplainOpen: false,
      isMapActive: false,
      isMapLoaded: false,
      isOfferHidden: false,
      isTouchDevice: false,
      otherPhotosCount: photos.length - 4,
      previewPhotos: miniPreviewPhotos,
    };
  }

  public getChildContext(): IOfferCardContext {
    const { config, logger } = this.props;

    const auctionMaxBet = this.props.offer.auction ? this.props.offer.auction.maxAvailableBet : 99;
    const newbuildingLabel = config.get('newbuildingLabels');

    return {
      api: this.props.api,
      baseUrl: this.props.baseUrl,
      currentSubdomain: this.props.currentSubdomain,
      defaultEmail: this.props.defaultEmail || '',
      defaultPhone: this.props.defaultPhone || '',
      experiments: this.props.experiments,
      httpApi: this.props.httpApi,
      isMlSearchForAll: config.get<boolean>('isMlSearchForAll.Enabled') || false,
      isSimilar: this.props.isSimilar,
      jsonQuery: this.props.jsonQuery,
      logger,
      mlSearchRegions: config.get<number[]>('mlSearchRegions') || [],
      offer: this.props.offer,
      offerHelper: offerHelper(this.props.offer),
      user: this.props.user,
      validateStep: config.get<boolean>('validateStep') || false,
      maxAuctionBet: this.props.maxAuctionBet,
      maxAuctionService: this.props.maxAuctionService,
      auctionMaxBet,
      countrysideOffersBuilderBadge: config.get<boolean>('Countryside.OffersBuilderBadge.Enabled') || false,
      newbuildingLabel: isRSNewbuildingLabel(newbuildingLabel, logger) ? newbuildingLabel as INewbuildingLabel : null,
      onlineShowEnabled: Boolean(config.get<boolean>('onlineShow.isEnabled')),
    };
  }

  public componentDidMount() {
    if (window) {
      this.setState({
        isTouchDevice: checkTouchDevice(),
      });
    }
  }

  public render() {
    const { api, offer, containerRef, isPromotionsInOffersActive, isOfferInSavedSearch } = this.props;
    const { previewPhotos, otherPhotosCount } = this.state;
    const agentPhone = getAgentPhone(offer);
    const callbackSettings = offer.workTimeInfo || {};
    const slicedPromotions = offer.kp && offer.kp.promotions && slicePromotions(offer.kp.promotions);
    const promotionAnalytics = getPromotionsAnalyticsData(offer.id, offer.kp);

    return (
      <div
        {...mergeStyles([
          offerCardStyles['offer-container'],
          this.state.isOfferHidden && offerCardStyles['offer-hidden'],
          this.props.offer.isTop3 && offerCardStyles['top3'],
          offer.isColorized && offerCardStyles['colorized'],
          this.props.isLightView ? offerCardStyles['light'] : null,
        ])}
        ref={containerRef}
      >
        <div
          {...mergeStyles([
            offerCardStyles['container'],
            this.state.isTouchDevice && offerCardStyles['touch-device'],
            this.checkSimilarLink() && offerCardStyles['offer-similar'],
            this.props.api.offerCard.isPopupsOpened() && offerCardStyles['offer-unclickable'],
          ])}
          onClick={this.handleOfferCardClick}
          onMouseEnter={this.handleOfferCardMouseEnter}
          onMouseLeave={this.handleOfferCardMouseLeave}
        >
          <div className={offerCardStyles['main-container']}>
            <div className={styles['media_brand_wrapper']}>
              <div className={styles['media_wrapper']}>
                <div {...mergeStyles([
                    styles['media'],
                    offer.photos.length <= 1 && styles['media--big'],
                  ])}>

                  {isPromotionsInOffersActive && slicedPromotions &&
                    <PromotionsBlock
                      promotions={slicedPromotions}
                      villageUrl={offer.fullUrl}
                      analyticsData={promotionAnalytics}
                    />
                  }
                  <Photos
                    offer={this.props.offer}
                    galleryControlsHidden={this.state.galleryControlsHidden}
                    trackPhotoSlideChanged={api.tracking && api.tracking.trackPhotoSlideChanged}
                    photoContainerStyle={
                      mergeStyles(
                        styles['photos_container'],
                        offer.photos.length <= 1 && styles['photos_container--big'],
                      ).className
                    }
                    layoutPhotoStyle={offer.fromDeveloper ? offerCardStyles['layout_image'] : undefined}
                    useThumbnail2Url={true}
                    isOfferInSavedSearch={isOfferInSavedSearch}
                  />
                </div>
                {offer.photos.length > 1 &&
                  <div className={styles['preview_photos']}>
                    {previewPhotos.map((photo, index) => {
                      if (!photo) {
                        return (
                          <div className={styles['preview_placeholder']} key={index}>
                            <PreviewPlaceholderIcon />
                          </div>
                        );
                      }

                      return (
                        <div className={styles['preview_photo']} key={index}>
                          {previewPhotos.length - 1 === index && otherPhotosCount > 1 &&
                            <div className={styles['other_photos']}>
                              <div className={styles['other_photos-content']}>
                                <div className={styles['other_photos-text']}>Еще фото</div>
                              </div>
                            </div>
                          }
                          <GalleryItemPreloader>
                            <GalleryImage
                              src={photo.thumbnailUrl}
                              alt=""
                            />
                          </GalleryItemPreloader>
                        </div>
                      );
                      },
                    )}
                  </div>
                }
              </div>
              <div className={styles['horizontal_agent_brand']}>
                <HorizontalAgentBrand />
              </div>
            </div>
            <div className={styles['main']}>
              {this.renderInfo()}
              <div className={styles['panels_container']}>
                {this.renderLeftPanel()}
                <RightPanel />
              </div>
            </div>
          </div>
          {
            (this.state.isCommentAreaOpened || offer.notes.offer || offer.notes.realtor) && (
              <div onClick={e => e.stopPropagation()} className={offerCardStyles['sub-block']}>
                <Comments
                  offer={offer}
                  isSimplified
                  onCompletion={this.handleCommentingCompletion}
                  {...api.comments}
                />
              </div>
            )
          }
          {this.renderSimilarLink()}
        </div>
        {offer.geo && offer.geo.coordinates && (
          <div {...mergeStyles([
            offerCardStyles['map-container'],
            this.state.isMapActive && offerCardStyles['map-active'],
          ])} >
            <div className={offerCardStyles['map']}>
              {this.state.isMapLoaded && <Map coordinates={offer.geo.coordinates}/>}
            </div>
          </div>
        )}
        {this.state.isCallbackModalOpened && !!agentPhone && (
          <CallbackModalContainer
            useOldWidget={offer.category !== 'newBuildingFlatSale'}
            gaLabel={offer.gaLabel || ''}
            agentPhone={agentPhone}
            {...callbackSettings}
            close={this.toggleCallbackModal}
            httpApi={this.props.httpApi}
          />
        )}
      </div>
    );
  }

  private renderInfo() {
    const { api, offer, position, isCianAgencyExperimentActive } = this.props;
    const { mortgageBanner, isTitleExperimentActive } = api;
    const { isByHomeowner, extensionLabels, offerInDeal } = offer;

    const isDealRentCianAgency = Boolean(offerInDeal && isCianAgencyExperimentActive);

    return (
      <div className={offerCardStyles['info-container']}>
        <div className={styles['info']}>
          <div className={`${styles['info_section']} ${offerCardStyles['main-info']}`}>
            <div>
              <a onClick={this.handleTitleClick}
                 href={offer.fullUrl}
                 className={offerCardStyles['header']}
                 target="_blank"
              >
                <TopTitle isDealRentCianAgency={isDealRentCianAgency} />
              </a>
              <div onClick={e => e.stopPropagation()}>
                <JkName />
              </div>
              <Deadline />
              <LeasePeriod />
              <LandStatus />
              <LandArea />
              {this.props.user && canUseHiddenBase(this.props.user) && Boolean(this.props.offer.isInHiddenBase) && (
                <HiddenBaseLabel containerStyle={offerCardStyles['base-label']}/>
              )}
            </div>
          </div>
          <div className={styles['address_info']}>
            <Address
              currentPath={this.props.currentPath}
              qsToUris={this.props.qsToUris}
              suggestionDistancesSeoText={this.props.suggestionDistancesSeoText}
            />
          </div>
          <div className={styles['price_flex_container']}>
            <TopPrice
              mortgageBanner={this.props.api.mortgageBanner}
              currentSubdomain={this.props.currentSubdomain}
              excludedUtilitiesTermsRegions={this.props.api.excludedUtilitiesTermsRegions}
              isDealRentCianAgency={isDealRentCianAgency}
            />
            {mortgageBanner}
          </div>
          <ExtendedOfferLabel labels={extensionLabels} labelStyle={styles['label']}/>
          <TopDescription
            {...mergeStyles(
              styles['info_section'],
              this.props.offer.isTop3 && offerCardStyles['top3-description'],
            )}
            isTitleExperimentActive={isTitleExperimentActive}
            onPageJumped={this.props.api.offerCard.onPageJumped}
          />
          <VillageSummary
            offer={offer}
            position={position}
          />
          <Pessimisation
            onComplainStateChanged={(isComplainOpen) => this.setState({ isComplainOpen })} />
        </div>

        {isDealRentCianAgency ? (
          <CianAgency />
        ) : (
          <>
            {isByHomeowner && <HomeownerBlock />}

            {!isByHomeowner &&
              (this.props.offer.user || this.props.offer.cianUserId) && (
                <div className={styles['agent_brand_wrapper']}>
                  <AgentBrand
                    position={this.props.position}
                    isSimilar={this.props.isSimilar}
                    isUsedOnTop3Card={true}
                  />
                </div>
              )}
          </>
        )}
      </div>
    );
  }

  private renderLeftPanel() {
    const { offer, complaintsHelpers, isHideOfferFetching, isOnHideAnimation } = this.props;
    const isPhoneHidden = !!offer.kp && !offer.kp.displayBuilderPhone;

    return (
      <TopButtons
        position={this.props.position}
        isMapActive={this.state.isMapActive}
        onHideFromPrintClicked={this.handleHideFromPrintClick}
        onMapButtonClick={this.handleButtonMapClick}
        isCallbackVisible={!!getAgentPhone(offer)}
        onCallbackClick={this.toggleCallbackModal}
        offer={offer}
        containerClassName={offerCardStyles['left-panel']}
        onCommentClick={this.handleCommentClick}
        buttonClassName={offerCardStyles['button']}
        dynamicButtonClassName={offerCardStyles['dynamic-button']}
        onFavoriteChange={this.props.api.offerCard.onFavoriteChange}
        isComplainOpen={this.state.isComplainOpen}
        onComplainClose={() => this.setState({ isComplainOpen: false })}
        isSimilar={this.props.isSimilar}
        isPhoneHidden={isPhoneHidden}
        isMoreInfoHidden={true}
        isHideOfferFetching={isHideOfferFetching || isOnHideAnimation}
        isSimplified
        complaintsHelpers={complaintsHelpers}
      />
    );
  }

  private renderSimilarLink() {
    if (!this.checkSimilarLink()) {
      return null;
    }

    const { offer } = this.props;
    const { category, similar } = offer;
    const similarCount = similar && similar.count;

    if (!similarCount) {
      return null;
    }

    return (
      <a
        {...mergeStyles([
          offerCardStyles['sub-block'],
          offerCardStyles['similar'],
        ])}
        href={(offer.similar as ISimilar).url}
        rel="nofollow noopener"
        target="_blank"
        onClick={this.handleSimilarClick}
      >
        {getSimilarLinkText(similarCount, category)}
      </a>
    );
  }

  private handleOfferCardClick = () => {
    const { isPopupsOpened, onPageJumped } = this.props.api.offerCard;
    if (isPopupsOpened()) {
      return;
    }
    onPageJumped('card_detail_in_newlist');
    window.open(this.props.offer.fullUrl, '_blank');
  }

  private handleOfferCardMouseEnter = () => this.setState({ galleryControlsHidden: false });

  private handleOfferCardMouseLeave = () => this.setState({ galleryControlsHidden: true });

  private handleTitleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    this.props.api.offerCard.onPageJumped('card_header_in_newlist');

    e.stopPropagation();
  }

  private handleSimilarClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const { offer: { kp, gaLabel }, api: { tracking }, position } = this.props;
    e.stopPropagation();

    if (gaLabel && tracking && tracking.trackMultiOfferClick) {
      if (kp) {
        trackSuburbanMultiOfferClick(kp.id, gaLabel, position);
      } else {
        tracking.trackMultiOfferClick(gaLabel);
      }
    }
  }

  private checkSimilarLink() {
    return Boolean(this.props.offer.similar && !this.props.jsonQuery.multi_id);
  }

  private handleHideFromPrintClick = () => {
    this.setState({
      isOfferHidden: true,
    });
  }

  private handleButtonMapClick = () => {

    this.setState({
      isMapActive: !this.state.isMapActive,
      isMapLoaded: true,
    });
  }

  private toggleCallbackModal = () => {
    this.setState({
      isCallbackModalOpened: !this.state.isCallbackModalOpened,
    });
  }

  private handleCommentClick = () => {
    if (this.state.isCommentAreaOpened) {
      return;
    }

    this.setState({
      isCommentAreaOpened: true,
    });
  }

  private handleCommentingCompletion = () => {
    if (!this.state.isCommentAreaOpened) {
      return;
    }

    this.setState({
      isCommentAreaOpened: false,
    });
  }
}
